// Display-only projections. Stored model precision and model calculations stay untouched.
export const finite = (n) => typeof n === "number" && Number.isFinite(n);
export function formatModelNumber(
  n,
  en = false,
  { digits = 2, percent = false, signed = false } = {}
) {
  if (!finite(n)) return "—";
  const value = n * (percent ? 100 : 1);
  const rounded = Number(value.toFixed(digits));
  return (
    (signed && rounded > 0 ? "+" : "") +
    (Object.is(rounded, -0) ? 0 : rounded).toLocaleString(
      en ? "en-GB" : "de-DE",
      { minimumFractionDigits: digits, maximumFractionDigits: digits }
    ) +
    (percent ? " %" : "")
  );
}
export const difference = (value, reference) =>
  finite(value) && finite(reference) ? value - reference : null;
export const goalTotal = (row) =>
  finite(row?.home) && finite(row?.away) ? row.home + row.away : null;
export function fixtureLayers(record, comparison, fixture) {
  const finished = fixture?.final === true && !!record?.evaluation;
  return [
    { id: "statistics", value: record?.pre?.statistic_xg },
    {
      id: "result",
      value:
        comparison?.result_model?.xg || record?.evaluation?.result_model_xg,
    },
    {
      id: "market",
      value: comparison?.market?.xg,
      total: comparison?.market?.total,
    },
    ...(finished
      ? [
          { id: "should", value: record.evaluation.should_goals },
          {
            id: "actual",
            value: fixture.goals || record.evaluation.actual_goals,
          },
        ]
      : []),
  ];
}
export function aggregateStatistics(records = []) {
  const groups = new Map();
  for (const r of records) {
    if (!r.evaluation) continue;
    for (const [id, e] of Object.entries(r.pre?.expected_statistics || {})) {
      const a = r.evaluation.actual_statistics?.[id];
      const scale = e.unit === "ratio" || id === "45" ? 100 : 1;
      if (!groups.has(id))
        groups.set(id, {
          id,
          title: e.title,
          percent: scale === 100,
          home: [],
          away: [],
          total: [],
        });
      const g = groups.get(id);
      for (const side of ["home", "away"])
        if (finite(e[side]) && finite(a?.[side]))
          g[side].push([e[side] * scale, a[side] * scale]);
      if (
        ["home", "away"].every((side) => finite(e[side]) && finite(a?.[side]))
      )
        g.total.push([(e.home + e.away) * scale, (a.home + a.away) * scale]);
    }
  }
  const summarize = (rows) => {
    if (!rows.length) return { count: 0 };
    const expected = rows.reduce((s, r) => s + r[0], 0),
      actual = rows.reduce((s, r) => s + r[1], 0),
      errors = rows.map((r) => r[1] - r[0]);
    const mean = (actual - expected) / rows.length;
    return {
      count: rows.length,
      expected,
      actual,
      difference: actual - expected,
      mean,
      mae: errors.reduce((s, e) => s + Math.abs(e), 0) / rows.length,
      sd: Math.sqrt(
        errors.reduce((s, e) => s + (e - mean) ** 2, 0) / rows.length
      ),
    };
  };
  return [...groups.values()].map((g) => ({
    ...g,
    home: summarize(g.home),
    away: summarize(g.away),
    total: summarize(g.total),
  }));
}
